import { useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { BookingPeriodDateFields } from "@/components/ui/BookingPeriodDateFields";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { PartyCountField } from "@/components/ui/PartyCountField";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { api, ApiError } from "@/lib/apiClient";
import { formatStayAmount } from "@/lib/money";
import { stripePaymentAlert } from "@/lib/stripePaymentStatus";
import {
  canShowBookingConfirmationPdf,
  fetchBookingConfirmationPdf,
} from "@/lib/bookingConfirmationPdf";
import { useCurrentUser } from "@/lib/auth";
import { AuditHistory } from "@/components/audit/AuditHistory";
import { CheckInFlowModal } from "@/components/bookings/CheckInFlowModal";
import { readGuestTabletWorkstationCode } from "@/lib/guestTabletWorkstationStorage";
import { OpenedInvoiceDraftModal } from "@/components/invoices/OpenedInvoiceDraftModal";
import { BookingInvoicesPanel } from "@/pages/bookings/BookingInvoicesPanel";
import {
  BOOKING_STATUS_LABELS,
  bookingStatusLabel,
  type BookingStatus,
} from "@/lib/bookingStatus";
import {
  addDaysLocal,
  dateValidationMessage,
  formatDateTimeShort,
  todayLocal,
  validateBookingDates,
} from "@/lib/bookingDates";
import { ComfortCheckInMark } from "@/components/bookings/ComfortCheckInMark";
import { PitchPlanBadge } from "@/components/bookings/PitchPlanBadge";
import { PitchPlanPanel } from "@/components/bookings/PitchPlanPanel";
import {
  PITCH_ASSIGNMENT_MODES,
  pitchAssignmentLabel,
  pitchMatchesBookingCategory,
  type PitchAssignmentMode,
} from "@/lib/pitchAssignment";
import { BookingCompanionsPanel } from "@/components/bookings/BookingCompanionsPanel";
import {
  InquiryEmailModal,
  type InquiryResponseIntent,
} from "@/components/bookings/InquiryEmailModal";
import { PlaceBanConfirmModal } from "@/components/bookings/PlaceBanConfirmModal";
import {
  confirmBookingInquiry,
  confirmRejectBookingInquiry,
  rejectBookingInquiry,
} from "@/lib/bookingInquiryActions";
import {
  collectPlaceBanGuestIds,
  liftPlaceBanForGuests,
  shouldConfirmPlaceBanBeforeInquiry,
} from "@/lib/placeBanActions";
import {
  VehicleFields,
  vehicleFieldsFromBooking,
  vehicleFieldsToApi,
  type VehicleFormValues,
} from "@/components/bookings/VehicleFields";
import { useArrivalTypes } from "@/lib/useArrivalTypes";
import { usePitchFeatures } from "@/lib/usePitchFeatures";
import { PitchFeatureSelect } from "@/components/pitches/PitchFeatureSelect";
import type { Booking, Guest, Pitch, PitchCategory } from "@/types/camping";

const BOOKING_STATUSES = Object.keys(BOOKING_STATUS_LABELS) as BookingStatus[];

export interface EditBookingModalProps {
  open: boolean;
  booking: Booking | null;
  guest: Guest | null;
  pitches: Pitch[];
  categories?: PitchCategory[];
  onClose: () => void;
  onSaved: () => void;
  canEdit: boolean;
  onOpenGuest?: (guest: Guest) => void;
  onOpenPitch?: (pitch: Pitch) => void;
}

export function EditBookingModal({
  open,
  booking,
  guest,
  pitches,
  categories = [],
  onClose,
  onSaved,
  canEdit,
  onOpenGuest,
  onOpenPitch,
}: EditBookingModalProps) {
  const { requiresVehicleLength } = useArrivalTypes();
  const { user } = useCurrentUser();
  const userGroups = (user?.groups as string[] | undefined) ?? [];
  const [liveBooking, setLiveBooking] = useState<Booking | null>(null);
  const [arrivalDate, setArrivalDate] = useState("");
  const [departureDate, setDepartureDate] = useState("");
  const [pitchId, setPitchId] = useState("");
  const [assignmentMode, setAssignmentMode] = useState<PitchAssignmentMode>("fixed");
  const [status, setStatus] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [adults, setAdults] = useState("2");
  const [children, setChildren] = useState("0");
  const [dogs, setDogs] = useState("0");
  const [notes, setNotes] = useState("");
  const [requestedFeatureIds, setRequestedFeatureIds] = useState<string[]>([]);
  const { features: pitchFeatures } = usePitchFeatures(open);
  const [vehicle, setVehicle] = useState<VehicleFormValues>(vehicleFieldsFromBooking({}));
  const [checkInFlowOpen, setCheckInFlowOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [actionLoading, setActionLoading] = useState<
    | "check-out"
    | "confirm"
    | "reject"
    | "send-confirmation"
    | "confirmation-pdf"
    | "resend-completion"
    | null
  >(null);
  const [openedInvoiceId, setOpenedInvoiceId] = useState<string | null>(null);
  const [confirmSuccess, setConfirmSuccess] = useState(false);
  const [inquiryEmailOpen, setInquiryEmailOpen] = useState(false);
  const [inquiryIntent, setInquiryIntent] = useState<InquiryResponseIntent | null>(null);
  const [updateEmailOpen, setUpdateEmailOpen] = useState(false);
  const [confirmationOffer, setConfirmationOffer] = useState(false);
  const [confirmationSent, setConfirmationSent] = useState(false);
  const [pendingParentRefresh, setPendingParentRefresh] = useState(false);
  const [placeBanConfirmOpen, setPlaceBanConfirmOpen] = useState(false);
  const [placeBanConfirmLoading, setPlaceBanConfirmLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [settlementNote, setSettlementNote] = useState<string | null>(null);
  const [versionConflict, setVersionConflict] = useState(false);
  const [reloading, setReloading] = useState(false);

  const activeBooking = liveBooking ?? booking;

  useEffect(() => {
    if (!open || !activeBooking) {
      setSettlementNote(null);
      return;
    }
    if (activeBooking.deposit_settlement_summary) {
      setSettlementNote(activeBooking.deposit_settlement_summary);
      return;
    }
    const paid = Number(activeBooking.deposit_paid_amount ?? 0);
    if ((status !== "cancelled" && status !== "no_show") || paid <= 0) {
      setSettlementNote(null);
      return;
    }
    let ignore = false;
    api
      .get<{ summary: string }>(
        `/api/v1/bookings/${activeBooking.id}/deposit-settlement?status=${status}`,
      )
      .then((preview) => {
        if (!ignore) setSettlementNote(preview.summary);
      })
      .catch(() => {
        if (!ignore) setSettlementNote(null);
      });
    return () => {
      ignore = true;
    };
  }, [
    open,
    activeBooking?.id,
    activeBooking?.deposit_settlement_summary,
    activeBooking?.deposit_paid_amount,
    status,
  ]);

  const isTerminal =
    activeBooking?.status === "checked_out" || activeBooking?.status === "cancelled";

  useEffect(() => {
    if (!open || !booking) return;
    setLiveBooking(booking);
    setArrivalDate(booking.arrival_date);
    setDepartureDate(booking.departure_date);
    setPitchId(booking.pitch_id ?? "");
    setAssignmentMode(booking.pitch_assignment_mode ?? "fixed");
    setStatus(booking.status);
    setAdults(String(booking.adults ?? 2));
    setChildren(String(booking.children ?? 0));
    setDogs(String(booking.dogs ?? 0));
    setNotes(booking.notes ?? "");
    setRequestedFeatureIds(booking.requested_feature_ids ?? []);
    setVehicle(vehicleFieldsFromBooking(booking));
    setCheckInFlowOpen(false);
    setError(null);
    setVersionConflict(false);
    setSubmitting(false);
    setActionLoading(null);
    setConfirmSuccess(false);
    setInquiryEmailOpen(false);
    setInquiryIntent(null);
    setUpdateEmailOpen(false);
    setConfirmationOffer(false);
    setConfirmationSent(false);
    setPendingParentRefresh(false);
    setPlaceBanConfirmOpen(false);
  }, [open, booking]);

  function openInquiryEmail(intent: InquiryResponseIntent) {
    if (!activeBooking) return;
    if (shouldConfirmPlaceBanBeforeInquiry(activeBooking, intent)) {
      setPlaceBanConfirmOpen(true);
      return;
    }
    setInquiryIntent(intent);
    setInquiryEmailOpen(true);
  }

  async function handlePlaceBanConfirmProceed(liftPlaceBan: boolean) {
    if (!activeBooking) return;
    setPlaceBanConfirmLoading(true);
    setError(null);
    try {
      if (liftPlaceBan) {
        let guestForBan = guest;
        if (!guestForBan) {
          guestForBan = await api.get<Guest>(`/api/v1/guests/${activeBooking.guest_id}`);
        }
        await liftPlaceBanForGuests(collectPlaceBanGuestIds(activeBooking, guestForBan));
        onSaved();
      }
      setPlaceBanConfirmOpen(false);
      setInquiryIntent("confirm");
      setInquiryEmailOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Platzverbot konnte nicht aufgehoben werden.");
    } finally {
      setPlaceBanConfirmLoading(false);
    }
  }

  function applyBookingToForm(next: Booking) {
    setLiveBooking(next);
    setArrivalDate(next.arrival_date);
    setDepartureDate(next.departure_date);
    setPitchId(next.pitch_id ?? "");
    setAssignmentMode(next.pitch_assignment_mode ?? "fixed");
    setStatus(next.status);
    setAdults(String(next.adults ?? 2));
    setChildren(String(next.children ?? 0));
    setDogs(String(next.dogs ?? 0));
    setNotes(next.notes ?? "");
    setRequestedFeatureIds(next.requested_feature_ids ?? []);
    setVehicle(vehicleFieldsFromBooking(next));
  }

  async function reloadBookingFromServer() {
    if (!activeBooking) return;
    setReloading(true);
    setError(null);
    setVersionConflict(false);
    try {
      const list = await api.get<Booking[]>("/api/v1/bookings");
      const fresh = list.find((b) => b.id === activeBooking.id);
      if (!fresh) {
        setError("Buchung wurde nicht mehr gefunden.");
        return;
      }
      applyBookingToForm(fresh);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Neu laden fehlgeschlagen");
    } finally {
      setReloading(false);
    }
  }

  async function savePatch(body: Record<string, unknown>, options?: { notifyParent?: boolean }) {
    if (!activeBooking) return;
    const version = activeBooking.version ?? 1;
    const updated = await api.patch<Booking>(`/api/v1/bookings/${activeBooking.id}`, {
      version,
      ...body,
    });
    applyBookingToForm(updated);
    if (options?.notifyParent !== false) onSaved();
    return updated;
  }

  function guestStayChanged(): boolean {
    if (!activeBooking) return false;
    return (
      arrivalDate !== activeBooking.arrival_date ||
      departureDate !== activeBooking.departure_date ||
      (Number(adults) || 0) !== (activeBooking.adults ?? 2) ||
      (Number(children) || 0) !== (activeBooking.children ?? 0) ||
      (Number(dogs) || 0) !== (activeBooking.dogs ?? 0)
    );
  }

  function canOfferUpdatedConfirmation(): boolean {
    if (!activeBooking || !guest?.email) return false;
    if (!["confirmed", "reserved", "checked_in"].includes(status)) return false;
    return guestStayChanged();
  }

  function requestClose() {
    if (pendingParentRefresh) {
      setPendingParentRefresh(false);
      onSaved();
    }
    onClose();
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!activeBooking || !canEdit || isTerminal) return;

    const dateError = validateBookingDates(arrivalDate, departureDate);
    const validationMsg = dateValidationMessage(dateError);
    if (validationMsg) {
      setError(validationMsg);
      return;
    }

    setError(null);
    setSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        arrival_date: arrivalDate,
        departure_date: departureDate,
        status,
        adults: Number(adults) || 0,
        children: Number(children) || 0,
        dogs: Number(dogs) || 0,
        notes: notes.trim() || null,
        requested_feature_ids: requestedFeatureIds,
        ...vehicleFieldsToApi(vehicle),
      };
      const offerMail = canOfferUpdatedConfirmation();
      await savePatch(body, { notifyParent: !offerMail && !confirmationOffer });
      if (offerMail) {
        setPendingParentRefresh(true);
        setConfirmationOffer(true);
        setConfirmationSent(false);
      } else if (!confirmationOffer) {
        onClose();
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.message.includes("zwischenzeitlich")) {
        setVersionConflict(true);
        setError(
          "Die Buchung wurde zwischenzeitlich von einem anderen Benutzer geändert. Laden Sie die Daten neu, um fortzufahren.",
        );
      } else {
        setError(err instanceof Error ? err.message : "Fehler beim Speichern");
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function onConfirmInquiry(options?: { skipConfirmationEmail?: boolean }) {
    if (!activeBooking || !canEdit || activeBooking.status !== "requested") return;
    setError(null);
    setConfirmSuccess(false);
    setActionLoading("confirm");
    try {
      const updated = await confirmBookingInquiry(activeBooking.id, options);
      applyBookingToForm(updated);
      setConfirmSuccess(true);
      setInquiryEmailOpen(false);
      setInquiryIntent(null);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Bestätigung fehlgeschlagen");
      throw err;
    } finally {
      setActionLoading(null);
    }
  }

  async function onRejectInquiry(skipConfirm = false) {
    if (!activeBooking || !canEdit || activeBooking.status !== "requested") return;
    if (!skipConfirm && !confirmRejectBookingInquiry(activeBooking)) return;
    setError(null);
    setActionLoading("reject");
    try {
      const updated = await rejectBookingInquiry(activeBooking);
      applyBookingToForm(updated);
      setInquiryEmailOpen(false);
      setInquiryIntent(null);
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Ablehnung fehlgeschlagen");
      throw err;
    } finally {
      setActionLoading(null);
    }
  }

  async function onAssignPitch() {
    if (!activeBooking || !canEdit || !pitchId) return;
    setError(null);
    setAssigning(true);
    try {
      const updated = await api.post<Booking>(
        `/api/v1/bookings/${activeBooking.id}/assign-pitch`,
        { pitch_id: pitchId },
      );
      applyBookingToForm(updated);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Platzzuweisung fehlgeschlagen");
    } finally {
      setAssigning(false);
    }
  }

  async function openConfirmationPdf(mode: "preview" | "download") {
    if (!activeBooking) return;
    setError(null);
    setActionLoading("confirmation-pdf");
    try {
      await fetchBookingConfirmationPdf(activeBooking.id, activeBooking.booking_number, mode);
    } catch (err) {
      setError(err instanceof Error ? err.message : "PDF konnte nicht geladen werden.");
    } finally {
      setActionLoading(null);
    }
  }

  async function onCheckOut() {
    if (!activeBooking || !canEdit) return;
    setError(null);
    setActionLoading("check-out");
    try {
      const updated = await api.post<Booking>(`/api/v1/bookings/${activeBooking.id}/check-out`, {});
      applyBookingToForm(updated);
      onSaved();
      if (updated.opened_invoice_id) {
        setOpenedInvoiceId(updated.opened_invoice_id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Check-out fehlgeschlagen");
    } finally {
      setActionLoading(null);
    }
  }

  if (!activeBooking) return null;

  const pitch = activeBooking.pitch_id
    ? pitches.find((p) => p.id === activeBooking.pitch_id)
    : undefined;
  const categoryName =
    activeBooking.pitch_category_id != null
      ? categories.find((c) => c.id === activeBooking.pitch_category_id)?.name
      : "Ohne Kategorie";
  const assignablePitches = pitches.filter((p) =>
    pitchMatchesBookingCategory(activeBooking.pitch_category_id, p.category_id),
  );
  const canCheckIn = ["confirmed", "reserved", "requested"].includes(activeBooking.status);
  const canCheckOut = activeBooking.status === "checked_in";
  const readOnly = !canEdit || isTerminal;
  const today = todayLocal();
  const departureMin = arrivalDate ? addDaysLocal(arrivalDate, 1) : today;

  const checkInFlowPitch = activeBooking.pitch_id
    ? pitches.find((p) => p.id === activeBooking.pitch_id) ?? null
    : null;

  return (
    <>
      <CheckInFlowModal
        open={checkInFlowOpen && canCheckIn}
        booking={activeBooking}
        guest={guest}
        pitch={checkInFlowPitch}
        userGroups={userGroups}
        guestTabletWorkstationCode={
          typeof localStorage !== "undefined"
            ? readGuestTabletWorkstationCode()
            : null
        }
        onClose={() => setCheckInFlowOpen(false)}
        onCompleted={(updated) => {
          applyBookingToForm(updated);
          setCheckInFlowOpen(false);
          onSaved();
        }}
        onDraftAborted={(invoiceId, updated) => {
          applyBookingToForm(updated);
          setCheckInFlowOpen(false);
          setOpenedInvoiceId(invoiceId);
          onSaved();
        }}
      />
      <OpenedInvoiceDraftModal
        invoiceId={openedInvoiceId}
        open={!!openedInvoiceId}
        userGroups={userGroups}
        onClose={() => setOpenedInvoiceId(null)}
        onChanged={() => {
          void reloadBookingFromServer();
          onSaved();
        }}
      />
      <PlaceBanConfirmModal
        open={placeBanConfirmOpen}
        booking={activeBooking}
        loading={placeBanConfirmLoading}
        onClose={() => setPlaceBanConfirmOpen(false)}
        onProceed={handlePlaceBanConfirmProceed}
      />
      <InquiryEmailModal
        open={(inquiryEmailOpen && inquiryIntent !== null) || updateEmailOpen}
        bookingId={activeBooking?.id ?? null}
        bookingNumber={activeBooking?.booking_number ?? null}
        intent={updateEmailOpen ? "confirm" : (inquiryIntent ?? "confirm")}
        purpose={updateEmailOpen ? "booking-update" : "inquiry"}
        canEdit={canEdit}
        confirmLoading={actionLoading === "confirm"}
        rejectLoading={actionLoading === "reject"}
        onClose={() => {
          if (updateEmailOpen) {
            const sent = confirmationSent;
            setUpdateEmailOpen(false);
            setConfirmationSent(false);
            if (sent) requestClose();
            return;
          }
          setInquiryEmailOpen(false);
          setInquiryIntent(null);
        }}
        onSent={() => {
          if (updateEmailOpen) {
            setConfirmationSent(true);
            setConfirmationOffer(false);
            return;
          }
          onSaved();
        }}
        onConfirm={
          !updateEmailOpen && activeBooking.status === "requested" && canEdit
            ? (options) => onConfirmInquiry(options)
            : undefined
        }
        onReject={
          !updateEmailOpen && activeBooking.status === "requested" && canEdit
            ? () => onRejectInquiry(true)
            : undefined
        }
      />
      <Modal
      open={open}
      onClose={requestClose}
      title={`Buchung ${activeBooking.booking_number}`}
      description="Buchungsdetails und Bearbeitung"
      size="lg"
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="secondary" onClick={requestClose} disabled={submitting || !!actionLoading}>
            {canEdit && !isTerminal && !confirmationOffer ? "Abbrechen" : "Schließen"}
          </Button>
          {canEdit && !isTerminal ? (
            <Button type="submit" form="edit-booking-form" loading={submitting} disabled={!!actionLoading}>
              Speichern
            </Button>
          ) : null}
        </div>
      }
    >
      <form id="edit-booking-form" onSubmit={(e) => void onSubmit(e)} className="space-y-5">
        {confirmationOffer ? (
          <Alert variant="info" title="Änderung gespeichert">
            <p>
              Soll {guest?.email ? guest.email : "der Gast"} eine aktualisierte Buchungsbestätigung
              bekommen? E-Mail und PDF enthalten die neuen Daten, zum Beispiel die verschobene
              Anreise. Den Text können Sie vor dem Versand noch ergänzen.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button type="button" onClick={() => setUpdateEmailOpen(true)}>
                Bestätigung senden
              </Button>
              <Button type="button" variant="secondary" onClick={requestClose}>
                Nicht senden
              </Button>
            </div>
          </Alert>
        ) : null}
        <dl className="grid gap-2 rounded-lg border border-separator bg-fill-tertiary px-4 py-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-label-secondary">Gast</dt>
            <dd className="font-medium text-label">
              {guest && onOpenGuest ? (
                <button
                  type="button"
                  className="text-accent hover:underline"
                  onClick={() => onOpenGuest(guest)}
                >
                  {guest.last_name}, {guest.first_name}
                </button>
              ) : guest ? (
                `${guest.last_name}, ${guest.first_name}`
              ) : (
                "—"
              )}
            </dd>
          </div>
          <div>
            <dt className="text-label-secondary">Zuweisung</dt>
            <dd className="font-medium text-label">
              {pitchAssignmentLabel(activeBooking.pitch_assignment_mode)}
              {activeBooking.pitch_category_id != null || !activeBooking.pitch_id ? (
                <span className="block text-sm font-normal text-label-secondary">
                  Kategorie: {categoryName ?? "—"}
                </span>
              ) : null}
            </dd>
          </div>
          <div>
            <dt className="text-label-secondary">Stellplatz</dt>
            <dd className="font-medium text-label">
              {pitch && onOpenPitch ? (
                <button
                  type="button"
                  className="text-accent hover:underline"
                  onClick={() => onOpenPitch(pitch)}
                >
                  {pitch.code}
                  {pitch.label ? ` – ${pitch.label}` : ""}
                </button>
              ) : (
                <span className="text-amber-700 dark:text-amber-400">Noch nicht zugewiesen</span>
              )}
              <div className="mt-1">
                <PitchPlanBadge booking={activeBooking} />
              </div>
            </dd>
          </div>
          <div>
            <dt className="text-label-secondary">Status</dt>
            <dd className="flex flex-wrap items-center gap-1.5">
              <StatusBadge status={activeBooking.status} />
              <ComfortCheckInMark
                completed={activeBooking.comfort_check_in_completed}
                paymentStatus={activeBooking.comfort_check_in_payment_status}
              />
            </dd>
          </div>
          <div>
            <dt className="text-label-secondary">Gesamt (brutto)</dt>
            <dd className="font-medium tabular-nums text-label">
              {formatStayAmount(activeBooking.total_amount, activeBooking.currency, activeBooking.price_pending)}
            </dd>
          </div>
          {activeBooking.actual_arrival_at ? (
            <div className="sm:col-span-2">
              <dt className="text-label-secondary">Eingecheckt am</dt>
              <dd className="text-label">{formatDateTimeShort(activeBooking.actual_arrival_at)}</dd>
            </div>
          ) : null}
          {activeBooking.actual_departure_at ? (
            <div className="sm:col-span-2">
              <dt className="text-label-secondary">Ausgecheckt am</dt>
              <dd className="text-label">{formatDateTimeShort(activeBooking.actual_departure_at)}</dd>
            </div>
          ) : null}
        </dl>

        {!isTerminal ? (
          <PitchPlanPanel
            booking={activeBooking}
            pitches={pitches}
            canEdit={canEdit}
            draftArrival={arrivalDate}
            draftDeparture={departureDate}
            onUpdated={(updated) => {
              applyBookingToForm(updated);
              onSaved();
            }}
            onOpenPitch={onOpenPitch}
          />
        ) : null}

        {(stripePaymentAlert(activeBooking.comfort_check_in_payment_status, "Comfort Check-in") ||
          stripePaymentAlert(activeBooking.guest_completion_payment_status, "Anzahlung")) ? (
          <div
            className={
              activeBooking.comfort_check_in_payment_status === "failed" ||
              activeBooking.guest_completion_payment_status === "failed"
                ? "rounded-lg border border-status-red/40 bg-status-red/10 px-4 py-3 text-sm text-label"
                : "rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-label"
            }
            role="status"
          >
            {[
              stripePaymentAlert(activeBooking.comfort_check_in_payment_status, "Comfort Check-in"),
              stripePaymentAlert(activeBooking.guest_completion_payment_status, "Anzahlung"),
            ]
              .filter(Boolean)
              .join(" ")}
          </div>
        ) : null}

        {activeBooking.comfort_check_in_completed ? (
          <div
            className="rounded-lg border border-status-green/40 bg-status-green/10 px-4 py-3 text-sm text-label"
            role="status"
          >
            Comfort Check-in abgeschlossen
            {activeBooking.comfort_check_in_completed_at
              ? ` am ${formatDateTimeShort(activeBooking.comfort_check_in_completed_at)}`
              : ""}
            . Kennzeichen und Zahlung sind hinterlegt. Das Rechnungsdokument können Sie unten
            anzeigen.
          </div>
        ) : null}

        {activeBooking.guest_completion_pending && canEdit && !isTerminal ? (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 space-y-2">
            <p className="text-sm text-label">
              Schnellbuchung: Der Gast muss Adresse, Personen, Fahrzeug und Wünsche noch über den
              E-Mail-Link eintragen und die Buchung verbindlich bestätigen
              {activeBooking.guest_completion_expires_at
                ? ` (Frist: ${formatDateTimeShort(activeBooking.guest_completion_expires_at)})`
                : ""}
              . Bis dahin ist der Platz vorgemerkt; danach wird er automatisch freigegeben.
            </p>
            <Button
              type="button"
              variant="secondary"
              disabled={!!actionLoading || submitting}
              loading={actionLoading === "resend-completion"}
              onClick={() => {
                setActionLoading("resend-completion");
                void api
                  .post(`/api/v1/bookings/${activeBooking.id}/resend-completion-email`, {})
                  .then(() => {
                    setActionLoading(null);
                  })
                  .catch((e: unknown) => {
                    setActionLoading(null);
                    setError(e instanceof ApiError ? e.message : "E-Mail konnte nicht gesendet werden.");
                  });
              }}
            >
              Bestätigungslink erneut senden
            </Button>
          </div>
        ) : null}

        {activeBooking.status === "requested" && canEdit && !isTerminal ? (
          <div className="rounded-lg border border-status-orange/40 bg-status-orange/10 px-4 py-3 space-y-2">
            <p className="text-sm text-label">
              Diese Online-Anfrage wartet auf Ihre Freigabe. Mit „Anfrage bestätigen“ oder
              „Anfrage ablehnen“ öffnen Sie ein Fenster zum Bearbeiten der E-Mail an den Gast.
              Nach der Bestätigung wird optional automatisch eine Bestätigungsmail versendet
              (siehe Stammdaten → Online-Buchung → Betrieb).
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                disabled={!!actionLoading || submitting}
                onClick={() => openInquiryEmail("confirm")}
              >
                Anfrage bestätigen
              </Button>
              <Button
                type="button"
                variant="danger"
                disabled={!!actionLoading || submitting}
                onClick={() => openInquiryEmail("reject")}
              >
                Anfrage ablehnen
              </Button>
            </div>
            {confirmSuccess ? (
              <p className="text-sm text-status-green">Buchung wurde bestätigt.</p>
            ) : null}
          </div>
        ) : null}

        {canShowBookingConfirmationPdf(activeBooking.status) ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              loading={actionLoading === "confirmation-pdf"}
              disabled={!!actionLoading || !!submitting}
              onClick={() => void openConfirmationPdf("preview")}
            >
              Bestätigung PDF anzeigen
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={!!actionLoading || !!submitting}
              onClick={() => void openConfirmationPdf("download")}
            >
              Bestätigung PDF speichern
            </Button>
            {guest?.email ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={!!actionLoading || !!submitting || guestStayChanged()}
                title={
                  guestStayChanged()
                    ? "Bitte zuerst speichern. Danach können Sie die aktualisierte Bestätigung senden."
                    : undefined
                }
                onClick={() => setUpdateEmailOpen(true)}
              >
                Buchungsbestätigung per E-Mail
              </Button>
            ) : null}
          </div>
        ) : null}

        <BookingInvoicesPanel
          booking={activeBooking}
          userGroups={userGroups}
          guestEmail={guest?.email}
          onChanged={onSaved}
        />

        {isTerminal ? (
          <p className="text-sm text-label-secondary">
            Diese Buchung kann nicht mehr bearbeitet werden (
            {bookingStatusLabel(activeBooking.status)}).
          </p>
        ) : (
          <fieldset className="space-y-3" disabled={readOnly}>
            <legend className="text-sm font-medium text-label">Aufenthalt</legend>
            <div className="grid min-w-0 gap-3 sm:grid-cols-2">
              <BookingPeriodDateFields
                embedded
                arrivalId="edit-booking-arrival"
                departureId="edit-booking-departure"
                arrivalLabel="Anreise"
                departureLabel="Abreise"
                arrival={arrivalDate}
                departure={departureDate}
                arrivalMin={today}
                departureMin={departureMin}
                onArrivalChange={setArrivalDate}
                onDepartureChange={setDepartureDate}
              />
              {assignmentMode === "fixed" ? (
                <div className="text-sm">
                  <span className="mb-1 block text-label-secondary">Stellplatz (fest)</span>
                  <p className="font-medium text-label">
                    {pitch
                      ? `${pitch.code}${pitch.label ? ` – ${pitch.label}` : ""}`
                      : "Noch nicht zugewiesen"}
                  </p>
                  <p className="mt-1 text-xs text-label-secondary">
                    Ändern oder Fixierung aufheben über „Stellplatzplanung“ mit Begründung.
                  </p>
                </div>
              ) : (
                <div className="space-y-2 sm:col-span-2">
                  <label className="text-sm block">
                    <span className="mb-1 block text-label-secondary">Stellplatz zuweisen</span>
                    <select
                      className="eh-select-input"
                      value={pitchId}
                      onChange={(e) => setPitchId(e.target.value)}
                      disabled={readOnly}
                    >
                      <option value="">— Auswählen —</option>
                      {assignablePitches.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.code}
                          {p.label ? ` – ${p.label}` : ""}
                        </option>
                      ))}
                    </select>
                  </label>
                  {!readOnly && pitchId && pitchId !== activeBooking.pitch_id ? (
                    <Button
                      type="button"
                      variant="secondary"
                      loading={assigning}
                      disabled={submitting || !!actionLoading}
                      onClick={() => void onAssignPitch()}
                    >
                      Platz zuweisen
                    </Button>
                  ) : null}
                  <p className="text-xs text-label-secondary">
                    Modus: {PITCH_ASSIGNMENT_MODES.find((m) => m.value === assignmentMode)?.description}
                  </p>
                </div>
              )}
              <label className="text-sm">
                <span className="mb-1 block text-label-secondary">Status</span>
                <select
                  className="eh-select-input"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  disabled={readOnly}
                >
                  {BOOKING_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {BOOKING_STATUS_LABELS[s]}
                    </option>
                  ))}
                </select>
              </label>
              {settlementNote ? (
                <p className="text-sm text-label-secondary sm:col-span-2">{settlementNote}</p>
              ) : null}
              {activeBooking.deposit_refund_error ? (
                <p className="text-sm text-status-red sm:col-span-2">{activeBooking.deposit_refund_error}</p>
              ) : null}
              <PartyCountField
                label="Erwachsene"
                value={adults}
                onChange={setAdults}
                disabled={readOnly}
              />
              <PartyCountField
                label="Kinder"
                value={children}
                onChange={setChildren}
                disabled={readOnly}
              />
              <PartyCountField label="Hunde" value={dogs} onChange={setDogs} disabled={readOnly} />
            </div>
            {pitchFeatures.length > 0 || requestedFeatureIds.length > 0 ? (
              <div className="space-y-1 border-t border-separator pt-3">
                <p className="text-sm font-medium text-label">
                  Wünsche (werden berücksichtigt, soweit möglich)
                </p>
                <PitchFeatureSelect
                  features={pitchFeatures}
                  selectedIds={requestedFeatureIds}
                  onChange={setRequestedFeatureIds}
                  disabled={readOnly}
                />
              </div>
            ) : null}
            <div className="space-y-2 border-t border-separator pt-3">
              <p className="text-sm font-medium text-label">Fahrzeug</p>
              <VehicleFields
                values={vehicle}
                onChange={setVehicle}
                disabled={readOnly}
                showVehicleLength={requiresVehicleLength(activeBooking.arrival_type)}
                vehicleLengthRequired={requiresVehicleLength(activeBooking.arrival_type)}
              />
            </div>
            {activeBooking.transponder_id || activeBooking.key_number ? (
              <div className="space-y-1 border-t border-separator pt-3 text-sm">
                <p className="font-medium text-label">Zugang</p>
                {activeBooking.transponder_id ? (
                  <p className="text-label-secondary">
                    Transponder: <span className="font-mono text-label">{activeBooking.transponder_id}</span>
                  </p>
                ) : null}
                {activeBooking.key_number ? (
                  <p className="text-label-secondary">
                    Schlüssel: <span className="font-mono text-label">{activeBooking.key_number}</span>
                  </p>
                ) : null}
              </div>
            ) : null}
            <BookingCompanionsPanel companions={activeBooking.companions} />
            <label className="block text-sm">
              <span className="mb-1 block text-label-secondary">Nachricht</span>
              <textarea
                className="eh-input min-h-[4rem] resize-y"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                disabled={readOnly}
              />
            </label>
          </fieldset>
        )}

        {canEdit && !isTerminal ? (
          <div className="flex flex-wrap gap-2">
            {activeBooking.status === "requested" ? (
              <>
                <Button
                  type="button"
                  disabled={!!actionLoading || submitting}
                  onClick={() => openInquiryEmail("confirm")}
                >
                  Anfrage bestätigen
                </Button>
                <Button
                  type="button"
                  variant="danger"
                  disabled={!!actionLoading || submitting}
                  onClick={() => openInquiryEmail("reject")}
                >
                  Anfrage ablehnen
                </Button>
              </>
            ) : null}
            {canCheckIn ? (
              <Button
                type="button"
                variant="secondary"
                disabled={!!actionLoading}
                onClick={() => setCheckInFlowOpen(true)}
              >
                Einchecken
              </Button>
            ) : null}
            {canCheckOut ? (
              <Button
                type="button"
                variant="secondary"
                loading={actionLoading === "check-out"}
                disabled={!!actionLoading}
                onClick={() => void onCheckOut()}
              >
                Auschecken
              </Button>
            ) : null}
          </div>
        ) : null}

        {activeBooking ? (
          <AuditHistory entityType="booking" entityId={activeBooking.id} active={open} />
        ) : null}

        {error ? (
          <div className="space-y-2">
            <p className="text-sm text-status-red">{error}</p>
            {versionConflict ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                loading={reloading}
                disabled={submitting || !!actionLoading}
                onClick={() => void reloadBookingFromServer()}
              >
                Daten neu laden
              </Button>
            ) : null}
          </div>
        ) : null}

      </form>
    </Modal>
    </>
  );
}

